/**
 * BM-H578 (21 Sep 2026, May Ann via Vanessa):
 *   "can i have edit function for credit control's datagrid rows/record -
 *    for correction of invoice/due dates amount, stage, etc"
 *
 * One correction surface, used from both the Credit Control datagrid (pencil
 * on the row) and the record page (Edit record). Deliberately shared rather
 * than duplicated: the moment there are two editors for the same record, one
 * of them ends up missing a field and accounts corrects a figure that never
 * changes where they are looking.
 *
 * Scope is corrections only. Recording a payment, writing off, advancing a
 * stage as a chase action and disputing all keep their own evidenced routes;
 * this modal exists for the day-to-day reality that an invoice was keyed with
 * the wrong figure, date or stage. Every change here is audited old -> new on
 * the matter audit trail by the backend.
 *
 * BM-H587 (23 Sep 2026): the record's category and owner are set here too.
 * The categories and the owners come from the backend (/tag-options), which
 * reads them from its own list and from staff config, so this panel never
 * hardcodes a label or anyone's initials. The backend refuses a category the
 * record contradicts, and a Hold without a reason and a review-by date.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Save, PoundSterling } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const STAGE_OPTIONS = [
  { value: "invoice_issued", label: "Invoice Issued" },
  { value: "reminder_1", label: "Reminder 1" },
  { value: "call_1", label: "Call 1" },
  { value: "reminder_2", label: "Reminder 2" },
  { value: "call_2", label: "Call 2" },
  { value: "reminder_3", label: "Reminder 3" },
  { value: "call_3", label: "Call 3" },
  { value: "lba_sent", label: "LBA Sent" },
  { value: "proceedings", label: "Proceedings" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "part_paid", label: "Part Paid" },
  { value: "paid", label: "Paid" },
  { value: "pending_match", label: "Pending Match" },
  { value: "payment_arrangement", label: "Payment Arrangement" },
  { value: "disputed", label: "Disputed" },
  { value: "written_off", label: "Written Off" },
  { value: "invoice_requested", label: "Invoice Requested" },
  { value: "pending_payment", label: "Pending Payment" },
];

function headers() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const email = user.user_email || user.email || user.ms_email ||
    localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id": localStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

/** A date input needs YYYY-MM-DD; the record may carry a full ISO stamp. */
function toDateInput(value) {
  const text = (value || "").trim();
  if (!text) return "";
  return text.slice(0, 10);
}

const LABEL = "block text-xs text-gray-400 mb-1";
const FIELD =
  "w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white " +
  "placeholder:text-gray-500 focus:outline-none focus:border-teal/50";

export default function EditCreditControlModal({
  open, onClose, onSaved, recordId, record: seedRecord,
}) {
  const [record, setRecord] = useState(seedRecord || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [tagOptions, setTagOptions] = useState({ categories: [], owners: [] });

  const hydrate = useCallback((doc) => {
    setRecord(doc);
    setForm({
      client_name: doc.client_name || "",
      fee_earner: doc.fee_earner || "",
      matter_title: doc.matter_title || "",
      invoice_date: toDateInput(doc.invoice_date),
      due_date: toDateInput(doc.due_date),
      escalation_stage: doc.escalation_stage || "invoice_issued",
      next_action: doc.next_action || "",
      next_action_due: toDateInput(doc.next_action_due),
      status: doc.status || "active",
      category: doc.category || "",
      owner_email: doc.owner_email || "",
      hold_reason: doc.hold_reason || "",
      hold_review_by: toDateInput(doc.hold_review_by),
    });
    setInvoices((doc.invoices || []).map(inv => ({
      original_number: String(inv.invoice_number || ""),
      invoice_number: String(inv.invoice_number || ""),
      amount_outstanding: String(inv.amount_outstanding ?? ""),
      due_date: toDateInput(inv.due_date),
      invoice_date: toDateInput(inv.invoice_date),
    })));
  }, []);

  // The datagrid hands us a slim row (no invoices[]), so the modal always
  // reloads the full record before offering to correct it. Editing a figure
  // that came from a stale list is how the wrong number gets saved twice.
  useEffect(() => {
    if (!open || !recordId) return;
    let cancelled = false;
    setError("");
    setLoading(true);
    axios.get(`${API}/credit-control/${recordId}`, { headers: headers() })
      .then(res => { if (!cancelled) hydrate(res.data || {}); })
      .catch(err => {
        if (!cancelled) setError(errorMessage(err, "Could not load this record."));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    axios.get(`${API}/credit-control/tag-options`, { headers: headers() })
      .then(res => { if (!cancelled) setTagOptions(res.data || { categories: [], owners: [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, recordId, hydrate]);

  if (!open) return null;

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setInvoiceField = (idx, key, value) =>
    setInvoices(list => list.map((inv, i) => i === idx ? { ...inv, [key]: value } : inv));

  const recordChanges = () => {
    if (!form || !record) return {};
    const changed = {};
    const compare = {
      client_name: record.client_name || "",
      fee_earner: record.fee_earner || "",
      matter_title: record.matter_title || "",
      invoice_date: toDateInput(record.invoice_date),
      due_date: toDateInput(record.due_date),
      escalation_stage: record.escalation_stage || "invoice_issued",
      next_action: record.next_action || "",
      next_action_due: toDateInput(record.next_action_due),
      status: record.status || "active",
      category: record.category || "",
      owner_email: record.owner_email || "",
      hold_reason: record.hold_reason || "",
      hold_review_by: toDateInput(record.hold_review_by),
    };
    Object.keys(compare).forEach(key => {
      if ((form[key] || "") !== (compare[key] || "")) changed[key] = form[key] || "";
    });
    // BM-H587: a Hold's reason and date only travel with a Hold.
    if (form.category !== "hold") {
      delete changed.hold_reason;
      delete changed.hold_review_by;
    }
    return changed;
  };

  const invoiceChanges = () => invoices.map(inv => {
    const source = (record.invoices || []).find(
      i => String(i.invoice_number || "") === inv.original_number,
    ) || {};
    const changed = {};
    if (inv.invoice_number !== String(source.invoice_number || "")) {
      changed.invoice_number = inv.invoice_number.trim();
    }
    const amount = inv.amount_outstanding === "" ? null : parseFloat(inv.amount_outstanding);
    if (amount !== null && !Number.isNaN(amount) &&
        Number(amount) !== Number(source.amount_outstanding ?? NaN)) {
      changed.amount_outstanding = amount;
    }
    if (inv.due_date !== toDateInput(source.due_date)) changed.due_date = inv.due_date;
    if (inv.invoice_date !== toDateInput(source.invoice_date)) changed.invoice_date = inv.invoice_date;
    return { number: inv.original_number, changed };
  }).filter(entry => Object.keys(entry.changed).length > 0);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const recordPatch = recordChanges();
      const lines = invoiceChanges();
      if (Object.keys(recordPatch).length === 0 && lines.length === 0) {
        setError("Nothing has been changed yet.");
        setSaving(false);
        return;
      }
      // Record first, then each corrected invoice line. Sequential on purpose:
      // if a line is rejected (duplicate number, bad date) the team sees which
      // one, rather than a pile of parallel failures.
      if (Object.keys(recordPatch).length > 0) {
        await axios.patch(`${API}/credit-control/${recordId}`, recordPatch, { headers: headers() });
      }
      for (const line of lines) {
        await axios.patch(
          `${API}/credit-control/${recordId}/invoices/${encodeURIComponent(line.number)}`,
          line.changed,
          { headers: headers() },
        );
      }
      const count = Object.keys(recordPatch).length + lines.length;
      toast.success(`Saved ${count} correction${count === 1 ? "" : "s"}`);
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      console.error("Edit credit control failed:", err);
      const detail = errorMessage(err, "Could not save the correction.");
      setError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="edit-cc-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold text-white">Edit record</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"
            data-testid="edit-cc-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Corrections only. Every change is recorded on the matter audit trail
          with its old and new value. Use Record Payment or Write Off to reduce
          a balance, so the reduction is evidenced.
        </p>

        {loading && <p className="text-sm text-gray-400 py-6">Loading record...</p>}

        {!loading && form && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Client name</label>
                <input className={FIELD} value={form.client_name}
                  onChange={e => setField("client_name", e.target.value)}
                  data-testid="edit-cc-client-name" />
              </div>
              <div>
                <label className={LABEL}>Fee earner</label>
                <input className={FIELD} value={form.fee_earner}
                  onChange={e => setField("fee_earner", e.target.value)}
                  data-testid="edit-cc-fee-earner" />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL}>Matter title</label>
                <input className={FIELD} value={form.matter_title}
                  onChange={e => setField("matter_title", e.target.value)}
                  data-testid="edit-cc-matter-title" />
              </div>
              <div>
                <label className={LABEL}>Invoice date</label>
                <input type="date" className={FIELD} value={form.invoice_date}
                  onChange={e => setField("invoice_date", e.target.value)}
                  data-testid="edit-cc-invoice-date" />
              </div>
              <div>
                <label className={LABEL}>Due date</label>
                <input type="date" className={FIELD} value={form.due_date}
                  onChange={e => setField("due_date", e.target.value)}
                  data-testid="edit-cc-due-date" />
              </div>
              <div>
                <label className={LABEL}>Stage</label>
                <select className={FIELD} value={form.escalation_stage}
                  onChange={e => setField("escalation_stage", e.target.value)}
                  data-testid="edit-cc-stage">
                  {STAGE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL}>Status</label>
                <select className={FIELD} value={form.status}
                  onChange={e => setField("status", e.target.value)}
                  data-testid="edit-cc-status">
                  {STATUS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL}>Next action</label>
                <select className={FIELD} value={form.next_action}
                  onChange={e => setField("next_action", e.target.value)}
                  data-testid="edit-cc-next-action">
                  <option value="">None</option>
                  {STAGE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL}>Next action due</label>
                <input type="date" className={FIELD} value={form.next_action_due}
                  onChange={e => setField("next_action_due", e.target.value)}
                  data-testid="edit-cc-next-action-due" />
              </div>
              <div>
                <label className={LABEL}>Category</label>
                <select className={FIELD} value={form.category}
                  onChange={e => setField("category", e.target.value)}
                  data-testid="edit-cc-category">
                  <option value="">Untagged</option>
                  {(tagOptions.categories || []).map(c => (
                    <option key={c.value} value={c.value} title={c.help}>{c.label}</option>
                  ))}
                </select>
                {(() => {
                  const chosen = (tagOptions.categories || []).find(c => c.value === form.category);
                  return chosen ? (
                    <p className="text-xs text-gray-500 mt-1" data-testid="edit-cc-category-help">{chosen.help}</p>
                  ) : null;
                })()}
              </div>
              <div>
                <label className={LABEL}>Owner</label>
                <select className={FIELD} value={form.owner_email}
                  onChange={e => setField("owner_email", e.target.value)}
                  data-testid="edit-cc-owner">
                  <option value="">Unassigned</option>
                  {(tagOptions.owners || []).map(o => (
                    <option key={o.email} value={o.email}>{o.initials} ({o.name})</option>
                  ))}
                </select>
              </div>
              {form.category === "hold" && (
                <>
                  <div className="sm:col-span-2">
                    <label className={LABEL}>Reason for the Hold</label>
                    <input className={FIELD} value={form.hold_reason}
                      onChange={e => setField("hold_reason", e.target.value)}
                      placeholder="Why chasing has stopped"
                      data-testid="edit-cc-hold-reason" />
                  </div>
                  <div>
                    <label className={LABEL}>Review by</label>
                    <input type="date" className={FIELD} value={form.hold_review_by}
                      onChange={e => setField("hold_review_by", e.target.value)}
                      data-testid="edit-cc-hold-review-by" />
                  </div>
                </>
              )}
            </div>

            <h4 className="text-sm font-bold text-white mt-6 mb-2 flex items-center gap-2">
              <PoundSterling className="w-4 h-4 text-teal" /> Invoices
            </h4>
            {invoices.length === 0 ? (
              <p className="text-xs text-gray-500">No invoices on this record.</p>
            ) : (
              <div className="space-y-3">
                {invoices.map((inv, idx) => (
                  <div key={inv.original_number || idx}
                    className="border border-white/10 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-4 gap-2"
                    data-testid={`edit-cc-invoice-${inv.original_number}`}>
                    <div>
                      <label className={LABEL}>Invoice no.</label>
                      <input className={FIELD} value={inv.invoice_number}
                        onChange={e => setInvoiceField(idx, "invoice_number", e.target.value)}
                        data-testid={`edit-cc-invoice-number-${inv.original_number}`} />
                    </div>
                    <div>
                      <label className={LABEL}>Amount outstanding</label>
                      <input type="number" step="0.01" min="0" className={FIELD}
                        value={inv.amount_outstanding}
                        onChange={e => setInvoiceField(idx, "amount_outstanding", e.target.value)}
                        data-testid={`edit-cc-invoice-amount-${inv.original_number}`} />
                    </div>
                    <div>
                      <label className={LABEL}>Invoice date</label>
                      <input type="date" className={FIELD} value={inv.invoice_date}
                        onChange={e => setInvoiceField(idx, "invoice_date", e.target.value)}
                        data-testid={`edit-cc-invoice-invoice-date-${inv.original_number}`} />
                    </div>
                    <div>
                      <label className={LABEL}>Due date</label>
                      <input type="date" className={FIELD} value={inv.due_date}
                        onChange={e => setInvoiceField(idx, "due_date", e.target.value)}
                        data-testid={`edit-cc-invoice-due-date-${inv.original_number}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {error && (
          <p className="text-xs text-status-red mt-4" data-testid="edit-cc-error">{error}</p>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button size="sm" variant="outline" onClick={onClose}
            className="text-gray-300 border-white/20 text-xs" data-testid="edit-cc-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || loading || !form}
            className="bg-teal text-black hover:bg-teal/80 text-xs" data-testid="edit-cc-save">
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Saving..." : "Save corrections"}
          </Button>
        </div>
      </div>
    </div>
  );
}
